import { Combobox } from "@headlessui/react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Info, Check, X, ChevronDown } from "lucide-react";
import { AutoCompleteOption, AutoCompleteProps } from "./types";
import { orderByQuery } from "./utils";
import { autoCompleteStyles } from "./styles";

export const AutoComplete = ({
  size = "medium",
  label,
  tooltip,
  placeholder = "Select an option...",
  options,
  value,
  onChange,
  isDisplayed = true,
  addNewOption = true,
  readOnly = false,
  getAddOptionText,
}: AutoCompleteProps) => {
  const [query, setQuery] = useState("");
  const selectedItem = value
    ? options.find((item: AutoCompleteOption) => item.id === value) || null
    : null;

  const filteredItems =
    query === ""
      ? options
      : orderByQuery(
          options.filter((item: AutoCompleteOption) => {
            return item.label?.toLowerCase().includes(query.toLowerCase());
          }),
          query,
        );

  useEffect(() => {
    if (!isDisplayed && value) {
      onChange(null);
    }
  }, [isDisplayed, value, onChange]);

  const handleClear = () => {
    onChange("");
    setQuery("");
  };

  const onBlur = () => {
    setTimeout(() => {
      setQuery("");
    }, 100);
  };

  return (
    <div
      className={cn(autoCompleteStyles.container({ size }), {
        hidden: !isDisplayed,
      })}
    >
      {label && (
        <div className="flex items-center gap-2">
          <label className={autoCompleteStyles.label({ size })}>{label}</label>
          {tooltip && (
            <BasicTooltip content={tooltip}>
              <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
            </BasicTooltip>
          )}
        </div>
      )}
      <Combobox
        disabled={readOnly}
        value={selectedItem}
        onChange={(item) => {
          onChange(item?.id || null);
        }}
      >
        <div className="relative">
          <Combobox.Button className="flex w-full items-center justify-between">
            <Combobox.Input
              className={autoCompleteStyles.input({ size })}
              displayValue={(item: AutoCompleteOption | null) =>
                item?.label || value || ""
              }
              onBlur={onBlur}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder={placeholder}
              readOnly={readOnly}
            />
            {!readOnly && (
              <div className="absolute inset-y-0 right-1.5 flex cursor-pointer items-center gap-1 pr-2">
                {!addNewOption && (
                  <ChevronDown className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                )}
                {value && (
                  <X
                    onClick={handleClear}
                    className="h-5 w-5 text-gray-400 hover:text-gray-500"
                  />
                )}
              </div>
            )}
          </Combobox.Button>
        </div>
        <div className="relative">
          <Combobox.Options className={autoCompleteStyles.options({ size })}>
            {filteredItems.length === 0 && query !== "" ? (
              addNewOption ? (
                <Combobox.Option
                  className="relative cursor-pointer select-none py-2 pl-3 pr-4 text-gray-900 transition-colors hover:bg-gray-50"
                  value={{ id: query, value: query, label: query }}
                >
                  {getAddOptionText?.(query) ||
                    `Would you like to add ${query} as a new option?`}
                </Combobox.Option>
              ) : (
                <div className="relative py-2 pl-3 pr-4 text-gray-500">
                  No matches found for '{query}'.
                </div>
              )
            ) : (
              filteredItems?.map((item: AutoCompleteOption, index: number) => (
                <Combobox.Option
                  key={`${item.label}-${index}`}
                  value={item}
                  className={({ active }) =>
                    `relative mx-1 cursor-pointer select-none rounded-md py-2 pl-3 pr-9 transition-colors ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ active, selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {item?.label}
                      </span>
                      {selected && (
                        <span
                          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                            active ? "text-blue-600" : "text-blue-500"
                          }`}
                        >
                          <Check className="h-5 w-5" />
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
};
